import { LinkGuideLayout } from '../components/LinkGuideLayout';

const sections = [
  ['enable', 'Choose a folder and enable copies'],
  ['included', 'Know which files are copied'],
  ['retry', 'Recover a pending copy'],
  ['race-studio', 'Use copies in Race Studio'],
  ['race-studio-transfers', 'Review Race Studio transfers'],
] as const;

export function PaddiqLinkFileCopiesPage() {
  return (
    <LinkGuideLayout
      path="/resources/paddiq-link-file-copies"
      title="Save Link session files to a folder"
      lead="Keep an additional local copy of downloaded MyChron files and recover pending copies."
      sections={sections}
    >
      <section className="guide-step" id="enable">
        <div>
          <h2>Choose a folder and enable copies.</h2>
          <ol className="guide-instructions">
            <li>
              Open Link’s <strong>Settings</strong> and find <strong>Session file copies</strong>.
              On Windows, you can also open{' '}
              <strong>Integrations → Race Studio → Storage &amp; copies</strong>.
            </li>
            <li>
              Select <strong>Choose folder</strong> and choose an existing folder that this computer
              can write to. Connect any removable drive or network share first.
            </li>
            <li>
              Turn on <strong>Save downloaded files to a folder</strong>.
            </li>
            <li>Download a new session, then check the copied and pending counts in Settings.</li>
          </ol>
          <p>
            The option starts off and applies to your account on this computer. Set it up separately
            on another computer. If your installed build does not offer Session file copies, check
            the <a href="/resources/paddiq-link-troubleshooting">update instructions</a>.
          </p>
        </div>
      </section>
      <section className="guide-step" id="included">
        <div>
          <h2>Know which files are copied.</h2>
          <p>
            Only downloads first started while copying is enabled are included. Turning it on does
            not copy your existing library or downloads already started. Link organizes files using
            the displayed <strong>Naming rules</strong>, preserving their extension and contents.
            The default is Date / Driver folders and Driver_Vehicle_Track_Session type_Original
            filename. If a filename conflicts, Link adds a suffix instead of replacing the existing
            file.
          </p>
          <p>
            Cloud uploads and folder copies have separate statuses. Paddiq uploads can finish even
            if a copy fails. Cancelling an upload keeps its pending copy. A file in your chosen
            folder does not by itself confirm a successful Paddiq upload.
          </p>
          <p>
            <strong>Pause all</strong> does not pause folder copies. To pause copies, turn off{' '}
            <strong>Save downloaded files to a folder</strong>. Completed copies remain where they
            are; pending copies wait until you turn the option back on.
          </p>
        </div>
      </section>
      <section className="guide-step" id="retry">
        <div>
          <h2>Recover a pending copy.</h2>
          <ol className="guide-instructions">
            <li>Read the error under Session file copies in Settings.</li>
            <li>
              Reconnect a missing drive or share, check available space and write access, or use{' '}
              <strong>Choose folder</strong> to select another destination.
            </li>
            <li>
              Make sure copying is enabled, then choose <strong>Retry pending copies</strong>.
            </li>
            <li>Check the counts and confirm the files appear in the chosen folder.</li>
          </ol>
          <p>
            Changing folders or naming rules redirects pending copies and future copies. It does not
            move completed files from the old folder. Keep Link’s local data while copies are
            pending. For a persistent error, include the message and destination type when
            contacting support; do not send private session files unless support asks for them.
          </p>
        </div>
      </section>
      <section className="guide-step" id="race-studio">
        <div>
          <h2>Use copies in Race Studio.</h2>
          <p>
            On Windows, <strong>Integrations → Race Studio → Storage &amp; copies</strong> reads
            Race Studio’s configured folder and naming rules. <strong>Use for Link copies</strong>
            applies them to additional file copies and keeps the current on/off setting. Refresh and
            apply again after changing Race Studio’s preferences.
          </p>
          <p>
            A folder copy alone does not register the session in Race Studio’s database. To add
            sessions to the library, use Link’s separate <strong>Export</strong> tab or enable{' '}
            <strong>Automatically copy to Race Studio</strong> for new MyChron downloads. That
            workflow saves the files and updates the database directly.
          </p>
          <p>
            Return to <a href="/resources/paddiq-link-sessions">sessions and syncing</a> for
            choosing downloads and checking their Paddiq upload status.
          </p>
        </div>
      </section>
      <section className="guide-step" id="race-studio-transfers">
        <div>
          <h2>Review Race Studio transfers.</h2>
          <p>
            Open <strong>Integrations → Race Studio</strong> on Windows. <strong>Import</strong>
            sends selected session files to Paddiq; <strong>Export</strong> adds retained Paddiq
            originals to Race Studio. Driver and kart mappings are required; circuits are optional.
          </p>
          <p>
            Follow the <a href="/resources/paddiq-link-race-studio">Race Studio guide</a> for bulk
            mapping, creating missing profiles, renaming Race Studio drivers and vehicles, automatic
            exports, and troubleshooting. Plain file copies remain independent of these transfers.
          </p>
        </div>
      </section>
    </LinkGuideLayout>
  );
}
